import React, { useEffect, useState } from "react";

import { AuthContext } from "./auth/AuthContext.jsx";
import { detectAppLanguage } from "./i18n/detectLanguage.js";
import "./WebAuthGate.css";

function getTelegramInitData() {
  if (typeof window === "undefined") return "";
  return window.Telegram?.WebApp?.initData || "";
}

const WEB_AUTH_BG =
  "radial-gradient(circle at 0% 0%, #14b8a6 0, transparent 55%), radial-gradient(circle at 100% 100%, #0f172a 0, transparent 55%), #020817";

const COPY = {
  ru: {
    tagline: "Питание, прогресс и тренировки в одном приложении",
    today: "Сегодня",
    lunch: "Обед",
    kcal: "ккал",
    of: "из",
    protein: "Белки",
    fat: "Жиры",
    carbs: "Углеводы",
    weight: "Вес",
    metrics: "Замеры тела",
    workout: "Тренировка",
    exercise: "Жим лёжа",
    sets: "3 подхода",
    signIn: "Войти через Telegram",
    newTitle: "Впервые в FitMeUp?",
    newDesc: "Просто войдите через Telegram и начните свой путь.",
    returnTitle: "Уже пользовались?",
    returnDesc: "Продолжайте с того места, где остановились.",
    remembered: "После входа FitMeUp запомнит это устройство.",
    loading: "Загружаем...",
    cancelled: "Вход через Telegram отменён.",
    invalidState:
      "Не удалось безопасно подтвердить вход через Telegram. Попробуйте ещё раз.",
    failed: "Не удалось войти через Telegram. Попробуйте ещё раз.",
  },
  en: {
    tagline: "Nutrition, progress and workouts in one app",
    today: "Today",
    lunch: "Lunch",
    kcal: "kcal",
    of: "of",
    protein: "Protein",
    fat: "Fats",
    carbs: "Carbs",
    weight: "Weight",
    metrics: "Body measurements",
    workout: "Workout",
    exercise: "Bench press",
    sets: "3 sets",
    signIn: "Continue with Telegram",
    newTitle: "New to FitMeUp?",
    newDesc: "Simply sign in with Telegram and start your journey.",
    returnTitle: "Already used FitMeUp?",
    returnDesc: "Pick up where you left off.",
    remembered: "FitMeUp will remember this device after you sign in.",
    loading: "Loading...",
    cancelled: "Telegram sign-in was cancelled.",
    invalidState:
      "We couldn't securely verify your Telegram sign-in. Please try again.",
    failed: "Couldn't sign in with Telegram. Please try again.",
  },
};

function IconMeal() {
  return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 2v7a3 3 0 0 0 6 0V2M7 2v19M4 6h6M18 21V2c-3.5 2.6-4 6-4 11h4"/></svg>);
}

function IconScale() {
  return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M7 9a5.6 5.6 0 0 1 10 0H7Z"/><path d="M12 7.1v2.2M8 17h8"/></svg>);
}

function IconWorkout() {
  return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 9v6M5 6v12M8 10v4M8 12h8M16 10v4M19 6v12M22 9v6"/></svg>);
}

function IconTape() {
  return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="7" width="16" height="10" rx="4"/><path d="M8 10.5h4.5"/><path d="M8 13.8h1M10.7 13.8h1M13.4 13.8h1"/><path d="M17.2 10.3a2.2 2.2 0 1 1 0 3.8"/></svg>);
}

function IconCheck() {
  return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.05" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9.2"/><path d="m7.6 12.2 3 3L16.5 9.1"/></svg>);
}

export default function WebAuthGate({ children }) {
  const language = String(detectAppLanguage() || "").toLowerCase().startsWith("ru") ? "ru" : "en";
  const t = COPY[language];
  const isTelegram = Boolean(getTelegramInitData());

  const [status, setStatus] = useState(isTelegram ? "ready" : "checking");
  const [error, setError] = useState("");
  const [authUser, setAuthUser] = useState(null);

  useEffect(() => {
    if (isTelegram || status === "ready" || typeof document === "undefined") return undefined;
    const html = document.documentElement;
    const body = document.body;
    const previousHtmlBackground = html.style.background;
    const previousBodyBackground = body.style.background;
    html.style.background = WEB_AUTH_BG;
    body.style.background = WEB_AUTH_BG;
    return () => {
      html.style.background = previousHtmlBackground;
      body.style.background = previousBodyBackground;
    };
  }, [isTelegram, status]);

  useEffect(() => {
    if (isTelegram) {
      setStatus("ready");
      return;
    }

    let cancelled = false;

    async function checkWebSession() {
      try {
        const res = await fetch("/api/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            appLanguage: detectAppLanguage(),
            appLanguageSource: "auto",
          }),
        });

        if (cancelled) return;

        if (res.ok) {
          const json = await res.json().catch(() => null);
          if (!json?.user?.id) {
            setStatus("auth");
            return;
          }
          window.localStorage.setItem("fitmeup:webUserId", String(json.user.id));
          setAuthUser(json.user);
          setStatus("ready");
          return;
        }

        setStatus("auth");
      } catch (e) {
        console.error("[WebAuthGate] session check error:", e);
        if (!cancelled) setStatus("auth");
      }
    }

    checkWebSession();
    return () => { cancelled = true; };
  }, [isTelegram]);

  useEffect(() => {
    if (isTelegram || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("telegram_login") !== "error") return;

    const reason = String(params.get("reason") || "");
    const message = reason === "cancelled" ? t.cancelled : reason === "invalid_state" ? t.invalidState : t.failed;
    setError(message);
    params.delete("telegram_login");
    params.delete("reason");
    const nextSearch = params.toString();
    const nextUrl = window.location.pathname + (nextSearch ? `?${nextSearch}` : "") + window.location.hash;
    window.history.replaceState({}, "", nextUrl);
  }, [isTelegram, t.cancelled, t.failed, t.invalidState]);

  const handleTelegramLogin = () => {
    setError("");
    window.location.assign("/api/web-telegram-start");
  };

  if (status === "ready") {
    return (
      <AuthContext.Provider value={{ user: authUser, setUser: setAuthUser, isTelegram }}>
        {children}
      </AuthContext.Provider>
    );
  }

  if (status === "checking") {
    return (
      <main className="web-auth-page web-auth-loading" lang={language}>
        <div className="web-auth-logo">FitMe<span>Up</span></div>
        <p>{t.loading}</p>
      </main>
    );
  }

  return (
    <main className="web-auth-page" lang={language}>
      <div className="web-auth-shell">
        <header className="web-auth-header">
          <h1 className="web-auth-logo">FitMe<span>Up</span></h1>
          <p className="web-auth-subtitle">{t.tagline}</p>
        </header>

        <section className="web-auth-art" aria-label="FitMeUp features">
          <img className="web-auth-phone" src="/landing/phone.webp" alt="" />
          <div className="web-auth-today">{t.today} <span aria-hidden="true">⌄</span></div>
          <div className="web-auth-phone-calories">
            <strong>405</strong>
            <span>{t.kcal}</span>
            <small>{t.of} 2010</small>
          </div>

          <article className="web-auth-card web-auth-food-card">
            <div className="web-auth-card-heading">
              <span className="web-auth-card-icon web-auth-card-icon--svg"><IconMeal /></span>
              <strong>{t.lunch}</strong>
              <b>430 <small>{t.kcal}</small> ›</b>
            </div>
            <img className="web-auth-card-photo" src="/landing/fitmeup-auth-food.webp" alt="" loading="eager" decoding="async" fetchPriority="high" width="768" height="432" />
            <div className="web-auth-macros">
              {[[t.protein, "40", "37%", "protein"],[t.fat, "10", "21%", "fat"],[t.carbs, "45", "42%", "carbs"]].map(([label, grams, percent, key]) => (
                <div className="web-auth-macro" key={key}>
                  <span>{label}</span>
                  <b>{grams} {language === "ru" ? "г" : "g"}</b>
                  <i className={`web-auth-progress ${key}`}><em /></i>
                  <small>{percent}</small>
                </div>
              ))}
            </div>
          </article>

          <article className="web-auth-card web-auth-weight-card">
            <div className="web-auth-card-heading">
              <span className="web-auth-card-icon web-auth-card-icon--svg"><IconScale /></span>
              <strong>{t.weight}</strong>
            </div>
            <div className="web-auth-weight-value">83,6 <small>{language === "ru" ? "кг" : "kg"}</small></div>
            <div className="web-auth-weight-change">↓ <span>-11,4 {language === "ru" ? "кг" : "kg"}</span></div>
            <svg className="web-auth-chart" viewBox="0 0 300 105" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <linearGradient id="authGraphFill" x1="0" y1="0" x2="0" y2="1">
                  <stop stopColor="#04cdec" stopOpacity=".25" />
                  <stop offset="1" stopColor="#04cdec" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M0 91 C20 83 37 82 57 70 S91 50 112 57 S151 69 169 49 S205 28 222 38 S264 26 300 7 L300 105 L0 105Z" fill="url(#authGraphFill)" />
              <path d="M0 91 C20 83 37 82 57 70 S91 50 112 57 S151 69 169 49 S205 28 222 38 S264 26 300 7" stroke="#0fe6ff" strokeWidth="3" fill="none" />
              {[[57,70],[112,57],[169,49],[222,38],[300,7]].map(([cx,cy],i)=><circle key={i} cx={cx} cy={cy} r="5" fill="#fff" />)}
            </svg>
            <div className="web-auth-measure">
              
              <div>{t.metrics}<small>90 - 60 - 90</small></div>
              <b>›</b>
            </div>
          </article>

          <article className="web-auth-card web-auth-workout-card">
            <div className="web-auth-card-heading">
              <span className="web-auth-card-icon web-auth-card-icon--svg"><IconWorkout /></span>
              <strong>{t.workout}</strong>
              <b>›</b>
            </div>
            <img className="web-auth-card-photo" src="/landing/fitmeup-auth-workout.webp" alt="" loading="eager" decoding="async" fetchPriority="high" width="768" height="432" />
            <div className="web-auth-exercise"><b>{t.exercise}</b><span>{t.sets}</span></div>
            <div className="web-auth-sets">{[12,10,8].map(reps => <div key={reps}><span className="web-auth-check"><IconCheck /></span><span>50 {language === "ru" ? "кг" : "kg"}<small>× {reps}</small></span></div>)}</div>
          </article>
        </section>

        <div className="web-auth-action">
          <button className="web-auth-telegram-button" type="button" onClick={handleTelegramLogin}>
            <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path fill="currentColor" d="M21.7 3.4 18.5 19c-.24 1.1-.87 1.37-1.76.85l-4.88-3.6-2.35 2.26c-.26.26-.48.48-.98.48l.35-4.97 9.04-8.17c.39-.35-.09-.55-.61-.2L6.14 12.7l-4.82-1.5c-1.05-.33-1.07-1.05.22-1.55L20.4 2.38c.87-.32 1.63.2 1.3 1.02Z" /></svg>
            <span>{t.signIn}</span>
          </button>
          <div className="web-auth-help-text"><p><strong>{t.newTitle}</strong> {t.newDesc}</p><p><strong>{t.returnTitle}</strong> {t.returnDesc}</p></div>
          {error && <div className="web-auth-error" role="alert">{error}</div>}
          <p className="web-auth-footer-note">{t.remembered}</p>
        </div>
      </div>
    </main>
  );
}
